import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './app/App';
import './styles/tokens.css';
import './styles/ui.css';

/**
 * Atualização do aplicativo.
 *
 * NUNCA automática. Trocar o bundle no meio de uma inspeção é inaceitável, e
 * `registerType: 'prompt'` no vite.config garante que o service worker novo
 * fica esperando em vez de assumir sozinho.
 *
 * O que faltava — e é o que esta função acrescenta — era a trava do último
 * metro: ao clicar em "Atualizar", o recarregamento acontecia na hora, sem
 * olhar se havia mutação pendente na fila. A fila em si é durável e sobrevive
 * (IndexedDB), mas uma sincronização em curso é interrompida, e o que a pessoa
 * tiver digitado e ainda não salvo se perde com o recarregamento.
 *
 * Agora: se há pendência, o sistema tenta enviar primeiro e só recarrega com
 * confirmação explícita, dizendo quantos itens estão em jogo.
 */
function montarAvisoDeAtualizacao(aplicar: () => Promise<void>) {
  const bar = document.createElement('div');
  bar.className = 'sw-update';
  bar.setAttribute('role', 'status');

  const texto = document.createElement('span');
  texto.textContent = 'Nova versão disponível.';

  const espaco = document.createElement('span');
  espaco.className = 'banner__spacer';

  const botao = document.createElement('button');
  botao.type = 'button';
  botao.className = 'btn btn--sm btn--primary';
  botao.textContent = 'Atualizar';

  bar.append(texto, espaco, botao);
  document.body.appendChild(bar);

  botao.addEventListener('click', async () => {
    botao.disabled = true;
    try {
      const [{ countPending }, { flush }] = await Promise.all([
        import('./data/db'),
        import('./data/sync'),
      ]);

      let pendentes = await countPending();
      if (pendentes > 0) {
        texto.textContent = `Enviando ${pendentes} ${pendentes === 1 ? 'alteração' : 'alterações'} antes de atualizar…`;
        await flush();
        pendentes = await countPending();
      }

      if (pendentes > 0) {
        // Não insistir e não recarregar por conta própria: o trabalho continua
        // guardado no aparelho, e quem decide é quem está com a máquina na frente.
        texto.textContent =
          `${pendentes} ${pendentes === 1 ? 'alteração continua' : 'alterações continuam'} ` +
          `sem sinal. O trabalho está salvo no aparelho e será enviado depois. Atualizar agora?`;
        botao.textContent = 'Atualizar mesmo assim';
        botao.disabled = false;
        botao.addEventListener('click', () => void aplicar(), { once: true });
        return;
      }

      await aplicar();
    } catch {
      // Falhar ao conferir a fila não pode impedir a atualização para sempre.
      botao.disabled = false;
      botao.textContent = 'Atualizar mesmo assim';
      botao.addEventListener('click', () => void aplicar(), { once: true });
    }
  });
}

const updateSW = registerSW({
  onNeedRefresh() {
    montarAvisoDeAtualizacao(() => updateSW(true));
  },
});

/**
 * Sessão de teste, SOMENTE em desenvolvimento.
 *
 * O `import.meta.env.DEV` é substituído por `false` no build de produção, e o
 * bloco inteiro — incluindo o `import()` — é eliminado pelo minificador. O
 * teste `e2e-auth.build.test.ts` varre o bundle publicado para provar isso.
 */
async function iniciar() {
  if (import.meta.env.DEV) {
    const [{ restaurarSessaoDeTeste }, { supabase }] = await Promise.all([
      import('./dev/e2e-auth'),
      import('./data/supabase'),
    ]);
    await restaurarSessaoDeTeste(supabase, import.meta.env.VITE_SUPABASE_URL as string);
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void iniciar();
